// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { IFrame, TokenExIframeStyles, useTokenExFrame } from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import usePaymentDetailsCartState from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import usePaymentDetailsFormHandlers from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormHandlers";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import usePaymentDetailsFormValidation from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormValidation";
import CreditCardBillingAddressEntry, {
    CreditCardBillingAddressEntryStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardBillingAddressEntry";
import CreditCardDetailsEntry, {
    CreditCardDetailsEntryStyles,
    CreditCardDetailsValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import SavedPaymentProfileEntry, {
    SavedPaymentProfileEntryStyles,
    SavedPaymentProfileValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { generateTokenExFrameStyleConfig } from "@store/MobiusOverrides/TokenExFrame/TokenExFrameStyleConfig";
import { orderProps } from "@store/Themes/common/props/order";
import constants from "@store/constants";
import getInvoicesTotal from "@store/Framework/Common/Utilities/getInvoicesTotal";
import { useInvoicePaymentContext } from "@store/Framework/Components/InvoicePaymentContext/InvoicePaymentContext";
import { payInvoices, PayInvoicesApiParameter } from "@store/Modules/client-framework/src/Services/InvoiceService";
import scrollToId from "@toolkit/Framework/Common/Utilities/scrollToId";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { FC, FormEvent, useEffect, useRef } from "react";
import { css, useTheme } from "styled-components";

let tokenExIframe: IFrame | undefined;
let tokenExFrameStyleConfig: TokenExIframeStyles;

const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

export interface InvoicePaymentDetailsProps extends WidgetProps {}

export interface InvoicePaymentDetailsStyles {
    block?: BlockProps;
    creditCardAddress?: CreditCardBillingAddressEntryStyles;
    creditCardDetails?: CreditCardDetailsEntryStyles;
    errorInfosBox?: InfosBoxProps;
    fieldset?: InjectableCss;
    form?: InjectableCss;
    paymentDetailsHeading?: TypographyPresentationProps;
    paymentMethodAndPONumberContainer?: GridContainerProps;
    paymentMethodGridItem?: GridItemProps;
    paymentMethodSelect?: SelectPresentationProps;
    paymentProfileAddressHeading?: TypographyPresentationProps;
    paymentProfileBillingAddress?: any;
    paymentProfileDetailsHeading?: TypographyPresentationProps;
    paymentProfileDetailsContainer?: GridContainerProps;
    paymentProfileDetailsGridItem?: GridItemProps;
    paymentProfileEditCardLink?: LinkPresentationProps;
    savedPaymentProfile?: SavedPaymentProfileEntryStyles;
    subSection?: InjectableCss;
}

export const invoicePaymentDetailsStyles: InvoicePaymentDetailsStyles = {
    block: orderProps.block,
    subSection: orderProps.subSection,
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    paymentDetailsHeading: orderProps.subtitle,
    paymentMethodAndPONumberContainer: orderProps.grid?.container,
    paymentMethodGridItem: orderProps.grid?.item,
    paymentProfileAddressHeading: orderProps.subSectionTitle,
    paymentProfileDetailsHeading: orderProps.subSectionTitle,
    paymentProfileDetailsContainer: orderProps.grid?.container,
    paymentProfileDetailsGridItem: orderProps.grid?.item,
    errorInfosBox: { type: "error" },
};

const styles = invoicePaymentDetailsStyles;

const InvoicePaymentDetails: FC<InvoicePaymentDetailsProps> = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const history = useHistory();
    const savedPaymentsPageLink = usePageLinkByPageType("SavedPaymentsPage");
    const settingsCollection = useSettingsCollection();
    const bypassCvvForSavedCards = settingsCollection.cartSettings.bypassCvvForSavedCards;
    const { errorMessage, invoices, setIsSubmitting, setPaymentErrorMessage, setPaymentSuccess, paymentSuccess } =
        useInvoicePaymentContext();

    const paymentDetailsFormState = usePaymentDetailsFormState();
    const {
        paymentMethod,
        cardHolderName,
        cardNumber,
        cardType,
        expirationMonth,
        expirationYear,
        securityCode,
        useBillingAddress,
        address1,
        countryId,
        stateId,
        city,
        postalCode,
        paymentMethodError,
        address1Error,
        countryError,
        stateError,
        cityError,
        postalCodeError,
        paystandError,
        showFormErrors,
        setShowFormErrors,
        isCardNumberTokenized,
        setIsCardNumberTokenized,
        creditCardLastFourDigits,
        bankAccountLastFourDigits,
        isIframeLoaded,
        paymentMethodRef,
        address1Ref,
        countryRef,
        stateRef,
        cityRef,
        postalCodeRef,
        maskedCardNumber,
        setMaskedCardNumber,
    } = paymentDetailsFormState;

    const currentCart = useAppSelector(state => getCurrentCartState(state));
    const paymentDetailsCartState = usePaymentDetailsCartState(currentCart, paymentMethod, countryId);
    const {
        useTokenExGateway,
        countries,
        paymentOptions,
        paymentMethods: basePaymentMethods,
        paymentMethodDto,
        isCreditCard,
        isECheck,
        isPaymentProfile,
        selectedCountry,
    } = paymentDetailsCartState;
    const paymentMethods = basePaymentMethods?.filter(x => x.isCreditCard || x.isPaymentProfile);

    const paymentDetailsFormValidation = usePaymentDetailsFormValidation(
        paymentDetailsFormState,
        paymentDetailsCartState,
    );
    const {
        validatePaymentMethod,
        validateAddress1,
        validateCountry,
        validateState,
        validateCity,
        validatePostalCode,
    } = paymentDetailsFormValidation;

    const {
        handlePaymentMethodChange,
        handleSaveCardChange,
        handleCardHolderNameChange,
        handleCardNumberChange,
        handleCardTypeChange,
        handleExpirationMonthChange,
        handleExpirationYearChange,
        handleSecurityCodeChange,
        handleUseBillingAddressChange,
        handleAddressChange,
        handleCountryChange,
        handleStateChange,
        handleCityChange,
        handlePostalCodeChange,
        handleEditCardClick,
    } = usePaymentDetailsFormHandlers({
        paymentDetailsFormState,
        paymentDetailsFormValidation,
        paymentDetailsCartState,
        history,
        savedPaymentsPageLink,
        setPaymentMethodCallback: () => {},
    });

    const tokenExLibraryIsLoaded = useTokenExFrame();
    const billToState = useAppSelector(state => getCurrentBillToState(state));

    useEffect(() => {
        if (isCardNumberTokenized) {
            const parameters: PayInvoicesApiParameter = {
                amount: getInvoicesTotal(invoices),
                invoices: invoices.map(x => x.invoiceUniqueNumber as string),
                paymentProfileId: paymentMethod,
            };

            if (!isPaymentProfile && isCreditCard) {
                let country = "";
                let countryAbbreviation = "";
                let state = "";
                let stateAbbreviation = "";

                if (countries) {
                    const currentCountry = countries.find(x => x.id === countryId);
                    if (currentCountry) {
                        country = currentCountry.name;
                        countryAbbreviation = currentCountry.abbreviation;

                        const currentState = currentCountry.states?.find(x => x.id === stateId);
                        if (currentState) {
                            state = currentState.name;
                            stateAbbreviation = currentState.abbreviation;
                        }
                    }
                }

                parameters.creditCard = {
                    address1,
                    cardHolderName,
                    cardNumber,
                    cardType,
                    city,
                    country,
                    countryAbbreviation,
                    expirationMonth,
                    expirationYear,
                    maskedCardNumber,
                    postalCode,
                    securityCode,
                    state,
                    stateAbbreviation,
                    useBillingAddress,
                };
            }

            setIsSubmitting(true);

            payInvoices(parameters)
                .then(result => {
                    setPaymentErrorMessage("");
                    setPaymentSuccess(result);
                    dispatch({ type: "Store/Pages/InvoiceHistory/ClearSelectedInvoices" });
                })
                .catch(error => {
                    setPaymentErrorMessage(error.errorJson.errors[0].message);
                    setPaymentSuccess(undefined);
                    setIsCardNumberTokenized(false);
                    tokenExIframe?.reset();
                })
                .finally(() => {
                    setIsSubmitting(false);
                    scrollToId("invoice-payment-header");
                });
        }
    }, [isCardNumberTokenized]);

    let tokenName: string | undefined;
    const creditCardDetails = useRef<CreditCardDetailsValidation>(null);
    const savedPaymentProfile = useRef<SavedPaymentProfileValidation>(null);

    if (useTokenExGateway) {
        if (isPaymentProfile) {
            tokenName = paymentMethodDto!.name;
        } else if (isCreditCard) {
            tokenName = "";
        }
    }

    useEffect(() => {
        if (typeof tokenName !== "undefined") {
            dispatch(loadTokenExConfig({ token: tokenName, isECheck }));
        }
    }, [paymentMethod]);

    const tokenExConfigs = useAppSelector(state => state.context.tokenExConfigs);
    const tokenExConfig = typeof tokenName !== "undefined" ? tokenExConfigs[tokenName] : undefined;

    const validateForm = () => {
        const paymentMethodValid = validatePaymentMethod(paymentMethod);
        const address1Valid = validateAddress1(address1);
        const countryValid = validateCountry(countryId);
        const stateValid = validateState(stateId);
        const cityValid = validateCity(city);
        const postalCodeValid = validatePostalCode(postalCode);

        let creditCardValid = true;
        if (creditCardDetails?.current) {
            creditCardValid = creditCardDetails.current.validate();
        }

        if (savedPaymentProfile?.current) {
            creditCardValid = savedPaymentProfile?.current.validate();
        }

        if (!paymentMethodValid) {
            paymentMethodRef.current?.focus();
        } else if (!address1Valid) {
            address1Ref.current?.focus();
        } else if (!countryValid) {
            countryRef.current?.focus();
        } else if (!stateValid) {
            stateRef.current?.focus();
        } else if (!cityValid) {
            cityRef.current?.focus();
        } else if (!postalCodeValid) {
            postalCodeRef.current?.focus();
        }

        return (
            paymentMethodValid &&
            creditCardValid &&
            address1Valid &&
            countryValid &&
            stateValid &&
            cityValid &&
            postalCodeValid
        );
    };

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateForm()) {
            setShowFormErrors(true);
            return false;
        }

        if (useTokenExGateway && ((isPaymentProfile && !bypassCvvForSavedCards) || isCreditCard)) {
            creditCardDetails.current?.tokenize();
            savedPaymentProfile.current?.tokenize();
        }

        return false;
    };

    if (!tokenExFrameStyleConfig) {
        tokenExFrameStyleConfig = generateTokenExFrameStyleConfig({ theme });
    }

    if (
        !invoices ||
        !paymentOptions ||
        (useTokenExGateway && !tokenExLibraryIsLoaded) ||
        errorMessage ||
        paymentSuccess
    ) {
        return null;
    }

    const iframeName = useTokenExGateway ? "TokenEx" : undefined;

    return (
        <Block {...styles.block}>
            <StyledForm {...styles.form} id="invoicePaymentForm" onSubmit={handleFormSubmit} noValidate={true}>
                <StyledFieldSet {...styles.fieldset}>
                    <Typography {...styles.paymentDetailsHeading}>{translate("Payment Details")}</Typography>
                    {paymentMethodDto?.isPaymentProfile && paymentMethodDto.isPaymentProfileExpired && (
                        <InfosBox {...styles.errorInfosBox}>
                            {siteMessage("Checkout_PaymentProfileExpired")}{" "}
                            {savedPaymentsPageLink && (
                                <Link {...styles.paymentProfileEditCardLink} onClick={handleEditCardClick}>
                                    {translate("Edit Card")}
                                </Link>
                            )}
                        </InfosBox>
                    )}
                    <StyledWrapper {...styles.subSection}>
                        <GridContainer {...styles.paymentMethodAndPONumberContainer}>
                            <GridItem {...styles.paymentMethodGridItem}>
                                {paymentMethods && paymentMethods.length > 0 && (
                                    <>
                                        <Select
                                            {...styles.paymentMethodSelect}
                                            label={translate("Payment Method")}
                                            value={paymentMethod ?? paymentMethodDto?.name}
                                            onChange={handlePaymentMethodChange}
                                            required
                                            error={showFormErrors && paymentMethodError}
                                            ref={paymentMethodRef}
                                        >
                                            <option value="">{translate("Select Payment Method")}</option>
                                            {paymentMethods.map(method => (
                                                <option key={method.name} value={method.name}>
                                                    {method.isPaymentProfile
                                                        ? `${translate("{0}:", translate("Credit Card"))} ${
                                                              method.description || method.name
                                                          }`
                                                        : method.description || method.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </>
                                )}
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                    {paymentMethodDto?.isPaymentProfile &&
                        !paymentMethodDto.isPaymentProfileExpired &&
                        !bypassCvvForSavedCards && (
                            <StyledWrapper {...styles.subSection}>
                                <Typography {...styles.paymentProfileDetailsHeading}>
                                    {translate("Credit Card Details")}
                                </Typography>
                                <GridContainer {...styles.paymentProfileDetailsContainer}>
                                    <GridItem {...styles.paymentProfileDetailsGridItem}>
                                        <SavedPaymentProfileEntry
                                            iframe={iframeName}
                                            onSecurityCodeUpdate={handleSecurityCodeChange}
                                            extendedStyles={styles.savedPaymentProfile}
                                            tokenExConfig={tokenExConfig}
                                            tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                            paymentMethodDto={paymentMethodDto}
                                            updateCardIsTokenized={setIsCardNumberTokenized}
                                            ref={savedPaymentProfile}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </StyledWrapper>
                        )}
                    {paymentMethodDto?.isCreditCard && (
                        <StyledWrapper {...styles.subSection}>
                            <CreditCardDetailsEntry
                                canSaveCard={false} // Custom API does not handle saving cards (paymentOptions.canStorePaymentProfile)
                                iframe={iframeName}
                                onSaveCardUpdate={handleSaveCardChange}
                                onCardHolderNameUpdate={handleCardHolderNameChange}
                                onCardNumberUpdate={handleCardNumberChange}
                                onCardTypeUpdate={handleCardTypeChange}
                                onExpirationMonthUpdate={handleExpirationMonthChange}
                                onExpirationYearUpdate={handleExpirationYearChange}
                                onSecurityCodeUpdate={handleSecurityCodeChange}
                                availableCardTypes={paymentOptions.cardTypes ?? []}
                                availableMonths={paymentOptions.expirationMonths ?? []}
                                availableYears={paymentOptions.expirationYears ?? []}
                                extendedStyles={styles.creditCardDetails}
                                isIframeLoaded={isIframeLoaded}
                                error={showFormErrors ? paystandError : undefined}
                                bankAccountLastFourDigits={bankAccountLastFourDigits}
                                creditCardLastFourDigits={creditCardLastFourDigits}
                                tokenExConfig={tokenExConfig}
                                tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                updateCardIsTokenized={setIsCardNumberTokenized}
                                updateMaskedCardNumber={setMaskedCardNumber}
                                ref={creditCardDetails}
                            />
                        </StyledWrapper>
                    )}
                    {paymentMethodDto?.isCreditCard && (
                        <StyledWrapper {...styles.subSection}>
                            <CreditCardBillingAddressEntry
                                useBillTo={useBillingAddress}
                                onUseBillToChange={handleUseBillingAddressChange}
                                billTo={billToState.value}
                                address1={address1}
                                address1Ref={address1Ref}
                                onAddress1Change={handleAddressChange}
                                address1Error={showFormErrors ? address1Error : undefined}
                                country={countryId}
                                countryRef={countryRef}
                                onCountryChange={handleCountryChange}
                                countryError={showFormErrors ? countryError : undefined}
                                state={stateId}
                                stateRef={stateRef}
                                onStateChange={handleStateChange}
                                stateError={showFormErrors ? stateError : undefined}
                                city={city}
                                cityRef={cityRef}
                                onCityChange={handleCityChange}
                                cityError={showFormErrors ? cityError : undefined}
                                postalCode={postalCode}
                                postalCodeRef={postalCodeRef}
                                onPostalCodeChange={handlePostalCodeChange}
                                postalCodeError={showFormErrors ? postalCodeError : undefined}
                                availableCountries={countries ?? []}
                                availableStates={selectedCountry?.states}
                                extendedStyles={styles.creditCardAddress}
                            />
                        </StyledWrapper>
                    )}
                    {paymentMethodDto?.isPaymentProfile && !paymentMethodDto.isPaymentProfileExpired ? (
                        <StyledWrapper {...styles.subSection}>
                            <Typography {...styles.paymentProfileAddressHeading}>
                                {translate("Credit Card Address")}
                            </Typography>
                            <PaymentProfileBillingAddress
                                address={paymentMethodDto.billingAddress}
                                extendedStyles={styles.paymentProfileBillingAddress}
                            />
                        </StyledWrapper>
                    ) : null}
                </StyledFieldSet>
                <button id="invoicePaymentForm-submit" type="submit" aria-hidden="true" style={{ display: "none" }}>
                    {translate("grimco_invoiceHistory_submitPayment")}
                </button>
            </StyledForm>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: InvoicePaymentDetails,
    definition: {
        group: constants.widgetGroup.invoicePayment,
        displayName: "Details",
        allowedContexts: [constants.pageType.invoicePayment],
    },
};

export default widgetModule;
